/*
 * PDP Reviews Redesign (template suffix: pdp-redesign-D)
 *
 * Responsibilities:
 *   1) Inject Etsy-style accordions ("Item details", "Shipping & returns",
 *      "Did you know?", "Meet your seller") into the buybox right column,
 *      below the Add-to-cart CTA. We inject from JS so we don't fork the
 *      large prod section main-product-redesign.liquid.
 *   2) Limit the inline Yotpo reviews widget to 3 visible review cards
 *      and wire a "View all reviews" CTA that opens a dialog/modal,
 *      moves the widget into the modal (lifting the limit), and restores
 *      it on close.
 *   3) Pull a photo carousel from Yotpo — reviews that have images are
 *      surfaced as a horizontally scrollable strip in the "From the
 *      community" section. Clicking a tile opens the reviews modal.
 *   4) (Wired but no-op until APP_KEY is set) Replace MOCK reviews data
 *      with REAL Yotpo data from api-cdn.yotpo.com — bottom-line stats,
 *      Smart Topics, Custom Questions, Reviews Media.
 *
 * Loaded only when template.suffix == 'pdp-redesign-D'.
 */
(function () {
  'use strict';

  // Yotpo public widget app key (read-only). Source of truth lives at
  // src/react/constants.js so React + theme stay in sync.
  var YOTPO_APP_KEY = 'ouJYuhvJ1tJMjMmMOd2zpKisG0fmlzUxFM05tDko';

  var REVIEW_LIMIT = 4;

  // Track one-time bindings so init() can be re-run on shopify:section:load
  // (theme editor reload) without double-binding document/window listeners.
  // Per-element wiring inside the section is safe to re-run — the old DOM
  // and its listeners are garbage-collected with the replaced section.
  var onceFlags = {};
  function once(key, fn) { if (onceFlags[key]) return; onceFlags[key] = true; fn(); }

  // ============================================================
  // Buybox accordion injection (Etsy-style right column)
  // ============================================================
  function injectBuyboxAccordions() {
    var buybox = document.querySelector('.pdp-buybox');
    if (!buybox || buybox.dataset.accordionsInjected === '1') return;

    var dataEl = document.getElementById('pdp-redesign-d-buybox-data');
    var data = {};
    if (dataEl) {
      try { data = JSON.parse(dataEl.textContent || dataEl.innerHTML) || {}; } catch (e) { data = {}; }
    }

    var accordions = [
      buildItemDetailsAccordion(data),
      // Shipping accordion removed — the legacy .ship-card section below
      // the gallery already renders the full shipping data (Orders ship /
      // Renews / Ships from + details paragraph) directly from metafields.
      // Keeping both produced two visible "Shipping policy" blocks on D.
      buildDidYouKnowAccordion(data),
      // Meet your seller accordion removed — the legacy pdp-seo-seller-card
      // (now styled as "About this seller" accordion with avatar + vendor
      // name + Message seller link) is the single seller surface on D.
      // Keeping both produced duplicate seller blocks in the buybox column.
    ].filter(Boolean);

    if (!accordions.length) return;

    var container = document.createElement('div');
    container.className = 'buybox-accordions';
    accordions.forEach(function (html) {
      var wrap = document.createElement('div');
      wrap.innerHTML = html.trim();
      container.appendChild(wrap.firstChild);
    });

    var anchor =
      buybox.querySelector('.pdp-confidence') ||
      buybox.querySelector('[class*="confidence"]') ||
      buybox.querySelector('[class*="last-minute"]') ||
      null;

    if (anchor && anchor.parentElement === buybox) {
      buybox.insertBefore(container, anchor);
    } else {
      buybox.appendChild(container);
    }

    buybox.dataset.accordionsInjected = '1';
    setupAccordionReadMore(buybox);
    liftLegacyCardsIntoAccordions(buybox);
  }

  // Move the existing prod-side cards (Cratejoy Confidence, Shipping Policy,
  // About This Seller) into the matching accordion slot so we don't duplicate
  // the content. Existing handlers (e.g. data-app-contact-seller-open on the
  // Contact seller button) ride along with the moved nodes.
  function liftLegacyCardsIntoAccordions(buybox) {
    var confidence = document.querySelector('.cratejoy-confidence-body');
    // Accept either <section> (C/Default) or <details> (D-template accordion)
    var shipping = document.querySelector('[aria-label="Shipping policy"].ship-card');
    var sellerCard = document.querySelector('section.ship-card[aria-label="About this seller"]');
    var sellerBio = sellerCard && sellerCard.querySelector('.pdp-seller-card__body');

    var bioSlot = buybox.querySelector('[data-bb-seller-bio-slot]');

    // Did you know? renders the Etsy purchase-protection card directly;
    // hide the legacy .cratejoy-confidence-body in place to remove the
    // duplicate from the buybox.
    if (confidence) confidence.style.display = 'none';

    // Move the ship-card into the buybox accordion column, positioned
    // RIGHT AFTER the "Item details" accordion (first one) and BEFORE
    // "Did you know?". This puts the shipping info next to the rest of
    // the buybox right-column accordions instead of leaving it stranded
    // in the body area below the gallery. Per-user request: Shipping
    // Policy should come before Did you know?
    var accordionsContainer = buybox.querySelector('.buybox-accordions');
    if (shipping && accordionsContainer) {
      var didYouKnow = null;
      var children = accordionsContainer.children;
      for (var i = 0; i < children.length; i++) {
        // Identify the Did you know accordion by its title text.
        var summaryText = children[i].textContent || '';
        if (/did you know/i.test(summaryText)) {
          didYouKnow = children[i];
          break;
        }
      }
      if (didYouKnow) {
        accordionsContainer.insertBefore(shipping, didYouKnow);
      } else {
        // Fallback: just append at the end of the accordions container.
        accordionsContainer.appendChild(shipping);
      }
    }

    if (sellerBio && bioSlot) {
      bioSlot.appendChild(sellerBio);
      if (sellerCard && !sellerCard.querySelector('.pdp-seller-card__body')) {
        sellerCard.style.display = 'none';
      }
    }
  }

  function buildItemDetailsAccordion(data) {
    // Description prose + features bullets (cjx.features metafield) — Etsy
    // shows highlights as a bullet list above the prose. Guard the .map
    // call: cjx.features is usually a list metafield, but if it's ever
    // configured as a single-value type on a shop we'd throw and halt the
    // rest of the D-template init. Treat anything non-array as no
    // features.
    var featuresList = Array.isArray(data.features) ? data.features : [];
    var features = featuresList.length
      ? '<ul class="bb-features">' +
          featuresList.map(function (f) { return '<li>' + escapeHtml(decodeEntities(f)) + '</li>'; }).join('') +
        '</ul>'
      : '';
    var description = data.description
      ? '<p>' + escapeHtml(decodeEntities(data.description)).replace(/\n+/g, '</p><p>') + '</p>'
      : '';

    if (!features && !description) return '';

    var html = '<div class="bb-prose" data-bb-prose>' + features + description + '</div>';
    return accordionShell('Item details', html, true, true);
  }

  function buildShippingAccordion(data) {
    // Etsy-style icon rows. The "Cancel" row links to the seller-contact
    // modal (logged in) or login (logged out) — same control as the rest
    // of the PDP.
    var rows = [];
    if (data.shipNew) {
      // Initial-shipment ETA (cjx.new_orders_ship) — e.g. "within five
      // business days". Shown above the renewal row so shoppers see when
      // their FIRST box ships before the recurring cadence.
      rows.push({
        icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>',
        text: 'Orders ship <strong>' + escapeHtml(String(data.shipNew).toLowerCase()) + '</strong>'
      });
    }
    if (data.shipFollowing) {
      rows.push({
        icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>',
        text: '<strong>Renews ' + escapeHtml(String(data.shipFollowing).toLowerCase()) + '</strong>'
      });
    }
    rows.push({
      icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 8v13H3V8M1 3h22v5H1zM10 12h4"/></svg>',
      text: ''
        + '<span class="bb-tt" tabindex="0">'
        +   '<span class="bb-tt__trigger">Cancel anytime</span>'
        +   '<span class="bb-tt__pop" role="tooltip">'
        +     'Log in &rarr; Your Account &rarr; Subscriptions &rarr; Cancel. '
        +     'No fees. Effective before your next renewal.'
        +   '</span>'
        + '</span> &mdash; manage your subscription from your account'
    });
    if (data.shipDetails) {
      rows.push({
        icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13" rx="1"/><path d="M16 8h4l3 3v5h-7M5.5 18.5a2.5 2.5 0 1 0 5 0 2.5 2.5 0 0 0-5 0zM16.5 18.5a2.5 2.5 0 1 0 5 0 2.5 2.5 0 0 0-5 0z"/></svg>',
        text: escapeHtml(data.shipDetails)
      });
    }
    if (data.shipsFrom) {
      rows.push({
        icon: '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>',
        text: 'Ships from: <strong>' + escapeHtml(data.shipsFrom) + '</strong>'
      });
    }
    var contactBtn = data.customerLoggedIn
      ? '<button type="button" class="bb-ship-contact" data-app-contact-seller-open>Contact seller</button>'
      // Guests can use the form too — it now renders an email input when
      // customer is null. Same trigger for both states.
      : '<button type="button" class="bb-ship-contact" data-app-contact-seller-open>Contact seller</button>';
    var rowsHtml = rows.map(function (r) {
      return '<li class="bb-ship-row">' +
        '<span class="bb-ship-row__icon" aria-hidden="true">' + r.icon + '</span>' +
        '<span class="bb-ship-row__text">' + r.text + '</span>' +
        '</li>';
    }).join('');
    var html =
      '<ul class="bb-ship-list">' + rowsHtml + '</ul>' +
      '<p class="bb-ship-foot">' + contactBtn + ' for questions about your box.</p>';
    return accordionShell('Shipping policy', html, true, false);
  }

  function buildDidYouKnowAccordion(data) {
    // Etsy-style purchase-protection card. Skip the climate/carbon line
    // (not relevant to Cratejoy). The legacy .cratejoy-confidence-body is
    // hidden by liftLegacyCardsIntoAccordions(); we render this directly.
    var html =
      '<div class="bb-protection-card">' +
        '<svg class="bb-protection-card__icon" width="40" height="40" viewBox="0 0 64 64" fill="none">' +
          '<path d="M32 56 C 12 44 8 28 16 22 C 22 17 28 21 32 26 C 36 21 42 17 48 22 C 56 28 52 44 32 56 Z" fill="#cfd6e8" stroke="#1f3a8a" stroke-width="2.5" stroke-linejoin="round"/>' +
          '<path d="M22 30 L 30 38 L 44 24" stroke="#1f3a8a" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/>' +
        '</svg>' +
        '<div class="bb-protection-card__body">' +
          '<div class="bb-protection-card__title">Cratejoy Purchase Protection</div>' +
          '<p>Shop confidently on Cratejoy knowing if something goes wrong with an order, we\'ve got your back for all eligible purchases — <a href="/pages/legal-buyer-protection" target="_blank" rel="noopener">see program terms</a>.</p>' +
        '</div>' +
      '</div>';
    return accordionShell('Did you know?', html, true, false);
  }

  function buildMeetSellerAccordion(data) {
    if (!data.vendor) return '';
    var initials = data.vendor.split(/\s+/).map(function (w) { return w.charAt(0); }).join('').slice(0, 2).toUpperCase();
    var avatarHtml = data.vendorImage
      ? '<img src="' + escapeHtml(data.vendorImage) + '" alt="' + escapeHtml(data.vendor) + '" class="bb-seller__avatar-img">'
      : '<span class="bb-seller__avatar-initials">' + escapeHtml(initials) + '</span>';
    var shopUrl = data.vendorUrl || '#more-products';
    // Single Message Seller button for both logged-in and guest shoppers
    // — pl-pdp-contact-seller-form.liquid renders an email input when
    // customer is null, so guests don't need to log in first.
    var messageBtn = '<button type="button" class="bb-seller__message-btn" data-app-contact-seller-open>Message Seller</button>';
    var html =
      '<div class="bb-seller">' +
        '<div class="bb-seller__top">' +
          '<a href="' + escapeHtml(shopUrl) + '" class="bb-seller__avatar" aria-label="' + escapeHtml(data.vendor) + ' shop page">' +
            avatarHtml +
          '</a>' +
          '<div class="bb-seller__heading">' +
            '<a href="' + escapeHtml(shopUrl) + '" class="bb-seller__name">' + escapeHtml(data.vendor) + '</a>' +
            '<div class="bb-seller__owner">Owner of <a href="' + escapeHtml(shopUrl) + '">' + escapeHtml(data.vendor) + '</a></div>' +
          '</div>' +
        '</div>' +
        messageBtn +
        '<p class="bb-seller__response">This seller usually responds <strong>within 24 hours</strong>.</p>' +
        // Bio slot — "About This Seller" copy gets moved here.
        '<div class="bb-seller__bio" data-bb-seller-bio-slot></div>' +
      '</div>';
    return accordionShell('Meet your seller', html, false, false);
  }

  function accordionShell(title, innerHtml, openByDefault, allowClamp) {
    var cls = 'buybox-accordion' + (allowClamp ? ' buybox-accordion--clampable is-clamped' : '');
    return '' +
      '<details class="' + cls + '"' + (openByDefault ? ' open' : '') + '>' +
        '<summary class="buybox-accordion__summary">' +
          '<span>' + escapeHtml(title) + '</span>' +
          '<svg class="buybox-accordion__chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>' +
        '</summary>' +
        '<div class="buybox-accordion__content">' + innerHtml + '</div>' +
      '</details>';
  }

  // Two-stage "Read more" inside any accordion that owns a [data-bb-prose].
  // States: is-clamped → is-expanded → (no class). Each click of the in-content
  // button progresses one step. Re-collapsing is handled by closing the accordion.
  function setupAccordionReadMore(buybox) {
    var clampables = buybox.querySelectorAll('.buybox-accordion--clampable');
    clampables.forEach(function (acc) {
      var prose = acc.querySelector('[data-bb-prose]');
      if (!prose) {
        acc.classList.remove('is-clamped');
        return;
      }
      // Only clamp if the prose actually overflows the clamp height.
      requestAnimationFrame(function () {
        if (prose.scrollHeight <= prose.clientHeight + 4) {
          acc.classList.remove('is-clamped');
          return;
        }
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'bb-readmore';
        btn.textContent = 'Read more';
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          if (acc.classList.contains('is-clamped')) {
            acc.classList.remove('is-clamped');
            acc.classList.add('is-expanded');
            btn.textContent = 'Read full';
          } else if (acc.classList.contains('is-expanded')) {
            acc.classList.remove('is-expanded');
            btn.textContent = 'Show less';
          } else {
            acc.classList.add('is-clamped');
            btn.textContent = 'Read more';
          }
        });
        prose.parentNode.insertBefore(btn, prose.nextSibling);
      });
    });
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Decode HTML entities in metafield text. Shopify's strip_html keeps
  // entities like &amp; intact, and our escapeHtml would re-encode them
  // into &amp;amp; — so users see literal "&amp;" on screen.
  var _decodeTa;
  function decodeEntities(s) {
    if (s == null) return '';
    if (!_decodeTa) _decodeTa = document.createElement('textarea');
    _decodeTa.innerHTML = String(s);
    return _decodeTa.value;
  }

  // ============================================================
  // Inline Yotpo: limit to 3 visible reviews + "View all" modal
  // ============================================================
  // Yotpo Widget V3 renders review cards into the .yotpo-widget-instance
  // host. Class names vary across versions (e.g. .yotpo-review, .yotpo-list-item,
  // .yotpo-review-card, [data-testid="review-list-item"]). We use a
  // MutationObserver to detect new cards and add a `--clamped` class to any
  // beyond REVIEW_LIMIT — CSS hides them. When the modal opens we remove
  // the limit class so all cards become visible.
  var REVIEW_CARD_SELECTORS = [
    '[data-testid="review-list-item"]',
    '.yotpo-review',
    '.yotpo-list-item',
    '.yotpo-review-card',
    '.yotpo-existing-review-content'
  ];

  function findReviewCards(root) {
    var nodes = [];
    REVIEW_CARD_SELECTORS.some(function (sel) {
      var found = root.querySelectorAll(sel);
      if (found && found.length) {
        nodes = Array.prototype.slice.call(found);
        return true;
      }
      return false;
    });
    return nodes;
  }

  function applyReviewLimit(host, limit) {
    if (!host) return;
    var cards = findReviewCards(host);
    cards.forEach(function (card, i) {
      if (i >= limit) card.classList.add('rr-card-clamped');
      else card.classList.remove('rr-card-clamped');
    });
    // restructureReviewCards intentionally disabled — we now render
    // Yotpo's native review card layout (matches prod cratejoy.com).
    moveWriteReviewToHeader();
  }

  // Move Yotpo's Write A Review button into a slot next to the
  // "Reviews for this item" title (top-right of the dialog). Yotpo's
  // class names for this button vary widely across versions, so we
  // try a stack of selectors and fall back to a text-content scan.
  function moveWriteReviewToHeader() {
    var title = document.querySelector('.reviews-redesign__title');
    if (!title) return;
    var btn = document.querySelector('.reviews-redesign__yotpo [class*="write-review"] button')
      || document.querySelector('.reviews-redesign__yotpo button[class*="write-review"]')
      || document.querySelector('.reviews-redesign__yotpo button[class*="write-a-review"]')
      || document.querySelector('.reviews-redesign__yotpo [class*="write-question-review"] button')
      || document.querySelector('.reviews-redesign__yotpo [class*="new-review-btn"]')
      || document.querySelector('.reviews-redesign__yotpo [class*="yotpo-new-review-btn"]')
      || document.querySelector('.reviews-redesign__yotpo button[class*="cta"]')
      || document.querySelector('.reviews-redesign__yotpo [data-testid="write-review-btn"]')
      || document.querySelector('.reviews-redesign__yotpo [data-testid*="write"]');
    // Last resort: scan all buttons inside the inline host for one with
    // "write" in its label.
    if (!btn) {
      var allBtns = document.querySelectorAll('.reviews-redesign__yotpo button, .reviews-redesign__yotpo a[role="button"]');
      for (var i = 0; i < allBtns.length; i++) {
        var t = (allBtns[i].textContent || '').trim().toLowerCase();
        if (t.indexOf('write') !== -1 && t.indexOf('review') !== -1) { btn = allBtns[i]; break; }
      }
    }
    if (!btn) return;
    if (btn.dataset.rrMovedToHeader === '1') return;

    var headerRow = title.parentElement && title.parentElement.classList.contains('rr-header-row')
      ? title.parentElement
      : null;
    if (!headerRow) {
      headerRow = document.createElement('div');
      headerRow.className = 'rr-header-row';
      title.parentElement.insertBefore(headerRow, title);
      headerRow.appendChild(title);
    }
    var slot = headerRow.querySelector('.rr-header-row__cta');
    if (!slot) {
      slot = document.createElement('div');
      slot.className = 'rr-header-row__cta';
      headerRow.appendChild(slot);
    }
    // If the Yotpo button is wrapped (the wrapper often carries the
    // .yotpo-write-review class), move the wrapper rather than the button
    // alone — preserves Yotpo's click handler scope.
    var wrapper = btn.closest('[class*="write-review"], [class*="write-a-review"], [class*="new-review-btn"]');
    var nodeToMove = (wrapper && wrapper !== btn) ? wrapper : btn;
    slot.appendChild(nodeToMove);
    btn.dataset.rrMovedToHeader = '1';
    btn.classList.add('rr-write-review-pill');
  }

  // Etsy-style card restructure: stars-row + "This item" pill on the LEFT,
  // avatar + name | date on the RIGHT (same row), body text below spanning
  // full width. We do this with DOM moves instead of CSS grid because Yotpo
  // uses different class names per product/version, and CSS grid + absolute
  // positioning kept fighting their inline layout.
  function restructureReviewCards(cards) {
    cards.forEach(function (card) {
      if (card.dataset.rrRestructured === '1') return;
      var stars = card.querySelector('[data-testid="reviews-rating"]')
        || card.querySelector('[class*="star-rating"]')
        || card.querySelector('[class*="review-rating"]')
        || card.querySelector('[class*="yotpo-stars"]');
      var title = card.querySelector('[class*="review-title"]')
        || card.querySelector('[data-testid="review-title"]');
      var reviewerInfo = card.querySelector('[class*="reviewer-info"]')
        || card.querySelector('[class*="user-info"]')
        || card.querySelector('[class*="yotpo-header"]')
        || card.querySelector('[data-testid*="reviewer"]')
        || card.querySelector('[data-testid*="user"]');
      var nameEl = card.querySelector('[class*="reviewer-name"]')
        || card.querySelector('[class*="display-name"]')
        || card.querySelector('[class*="user-name"]')
        || card.querySelector('[class*="reviewer-display-name"]');
      var verifiedEl = card.querySelector('[class*="verified-buyer"]')
        || card.querySelector('[class*="verified-reviewer"]');
      var avatarEl = card.querySelector('.yotpo-custom-icon')
        || card.querySelector('[class*="reviewer-image"]')
        || card.querySelector('[class*="reviewer-avatar"]')
        || card.querySelector('[class*="user-avatar"]');
      var date = card.querySelector('[class*="review-date"]')
        || card.querySelector('[class*="yotpo-date"]');
      if (!stars && !reviewerInfo && !nameEl) return;

      var topRow = card.querySelector(':scope > .rr-card-top');
      if (!topRow) {
        topRow = document.createElement('div');
        topRow.className = 'rr-card-top';
        card.insertBefore(topRow, card.firstChild);
      }
      var starsSlot = document.createElement('div');
      starsSlot.className = 'rr-card-top__stars';
      var reviewerSlot = document.createElement('div');
      reviewerSlot.className = 'rr-card-top__reviewer';
      topRow.appendChild(starsSlot);
      topRow.appendChild(reviewerSlot);

      if (stars) {
        starsSlot.appendChild(stars);
        // Etsy adds a numeric rating + "This item" pill after the stars.
        var rating = stars.querySelectorAll('[class*="full"], [class*="filled"]').length;
        if (!rating) {
          var aria = stars.getAttribute('aria-label') || '';
          var m = aria.match(/([1-5])/);
          if (m) rating = Number(m[1]);
        }
        if (rating) {
          var num = document.createElement('span');
          num.className = 'rr-card-top__rating-num';
          num.textContent = String(rating);
          starsSlot.appendChild(num);
        }
        // "This item" pill intentionally not rendered — reviews come
        // from the same seller across different products, so the
        // per-row source distinction isn't meaningful here.
      }
      // Hide title — Etsy doesn't show a separate review title; just the
      // body text reads under the top row.
      if (title) title.style.display = 'none';

      // Pull verified + name out of the wrapper so we can order them.
      var verifiedFromWrap = reviewerInfo
        ? (reviewerInfo.querySelector('[class*="verified-buyer"]')
           || reviewerInfo.querySelector('[class*="verified-reviewer"]'))
        : null;
      var nameFromWrap = reviewerInfo
        ? (reviewerInfo.querySelector('[class*="reviewer-name"]')
           || reviewerInfo.querySelector('[class*="display-name"]')
           || reviewerInfo.querySelector('[class*="user-name"]'))
        : null;
      var verified = verifiedFromWrap || verifiedEl;
      var name = nameFromWrap || nameEl;

      // Single inline row on the right: [Avatar] [Name] | [Date].
      // No "Verified Buyer" text — avatar conveys it; matches Etsy.
      var nameDateRow = document.createElement('span');
      nameDateRow.className = 'rr-card-top__name-date';
      // Prefer Yotpo's native .yotpo-custom-icon (real profile image)
      // when it actually carries a background-image. Otherwise inject
      // a small colored fallback circle keyed by name hash so EVERY
      // review gets a visible icon next to the name.
      function avatarHasImage(el) {
        if (!el) return false;
        var bg = el.style.backgroundImage;
        if (!bg) {
          try { bg = window.getComputedStyle(el).backgroundImage; } catch (e) { bg = ''; }
        }
        // Truthy AND not the literal "none" AND contains a url(...).
        return !!(bg && bg !== 'none' && /url\(['"]?[^'")\s]+['"]?\)/.test(bg));
      }
      if (avatarEl && avatarHasImage(avatarEl)) {
        nameDateRow.appendChild(avatarEl);
      } else {
        // Drop the empty Yotpo icon (if any) so it doesn't reserve
        // space without rendering anything.
        if (avatarEl && avatarEl.parentNode) avatarEl.parentNode.removeChild(avatarEl);
        if (name && name.textContent.trim()) {
          var palette = ['#5e7d4c', '#1f2e6e', '#bf6634', '#2a5d70', '#7a4f8f', '#a64242', '#c89b3c', '#386b4f', '#995c2a', '#445b8c'];
          var src = name.textContent.trim();
          var hash = 0;
          for (var ci = 0; ci < src.length; ci++) hash = (hash * 31 + src.charCodeAt(ci)) >>> 0;
          var fallback = document.createElement('span');
          fallback.className = 'rr-card-top__avatar-fallback';
          fallback.setAttribute('aria-hidden', 'true');
          fallback.style.backgroundColor = palette[hash % palette.length];
          nameDateRow.appendChild(fallback);
        }
      }
      if (name) nameDateRow.appendChild(name);
      if (date) nameDateRow.appendChild(date);
      reviewerSlot.appendChild(nameDateRow);
      if (verified) verified.style.display = 'none';

      // Hide any wrapper that's now visually empty after the moves above
      // (original reviewer-info / yotpo-header / review-title parents are
      // often left wrapping a single display:none child — which keeps
      // children.length > 0 but shows nothing, while still reserving
      // vertical space via padding/margin/grid-row gap). Walk bottom-up so
      // hiding a leaf collapses its parent on the next pass.
      function isVisuallyEmpty(el) {
        if (el.textContent.trim()) return false;
        var kids = el.children;
        for (var i = 0; i < kids.length; i++) {
          var k = kids[i];
          if (k.style.display === 'none') continue;
          if (k.tagName === 'IMG' || k.tagName === 'SVG') return false;
          if (!isVisuallyEmpty(k)) return false;
        }
        return true;
      }
      var passes = 0;
      var didHide = true;
      while (didHide && passes < 4) {
        didHide = false;
        passes++;
        Array.prototype.forEach.call(card.querySelectorAll('div, section, header, p, span'), function (el) {
          if (el.style.display === 'none') return;
          if (el.classList.contains('rr-card-top') || el.closest('.rr-card-top')) return;
          if (isVisuallyEmpty(el)) {
            el.style.display = 'none';
            didHide = true;
          }
        });
      }

      card.dataset.rrRestructured = '1';
    });
  }

  function clearReviewLimit(host) {
    if (!host) return;
    findReviewCards(host).forEach(function (card) {
      card.classList.remove('rr-card-clamped');
    });
  }

  function setupInlineReviewLimit() {
    var inline = document.querySelector('.reviews-redesign__yotpo');
    if (!inline) return null;

    inline.classList.add('reviews-redesign__yotpo--limited');

    // Initial pass + observer for late renders.
    applyReviewLimit(inline, REVIEW_LIMIT);
    var obs = new MutationObserver(function () {
      // While the inline host carries the --limited class, keep clamping.
      if (inline.classList.contains('reviews-redesign__yotpo--limited')) {
        applyReviewLimit(inline, REVIEW_LIMIT);
      } else {
        clearReviewLimit(inline);
      }
    });
    obs.observe(inline, { childList: true, subtree: true });
    return inline;
  }

  // Modal: lift the existing widget into the dialog (single Yotpo instance,
  // so we move DOM nodes rather than try to render a second instance).
  function setupReviewsModal(inlineHost) {
    var modal = document.getElementById('rr-reviews-modal');
    var openers = document.querySelectorAll('[data-rr-open-reviews]');
    if (!modal || !openers.length || !inlineHost) return;

    var slot = modal.querySelector('[data-rr-modal-slot]');
    var closeBtn = modal.querySelector('[data-rr-modal-close]');
    var backdrop = modal.querySelector('[data-rr-modal-backdrop]');
    var inlinePlaceholder = document.createComment('rr-yotpo-slot');
    var galleryPlaceholder = document.createComment('rr-gallery-slot');

    function moveGalleryIntoSlot() {
      var gallery = document.querySelector('.reviews-redesign__gallery');
      if (!gallery || !gallery.parentNode) return;
      if (galleryPlaceholder.parentNode) return;
      gallery.parentNode.insertBefore(galleryPlaceholder, gallery);
      gallery.classList.add('reviews-redesign__gallery--in-modal');
      slot.appendChild(gallery);
    }
    function restoreGalleryFromSlot() {
      if (!galleryPlaceholder.parentNode) return;
      var gallery = slot.querySelector('.reviews-redesign__gallery');
      if (gallery) {
        gallery.classList.remove('reviews-redesign__gallery--in-modal');
        galleryPlaceholder.parentNode.insertBefore(gallery, galleryPlaceholder);
      }
      galleryPlaceholder.parentNode.removeChild(galleryPlaceholder);
    }

    function openModal() {
      if (!inlineHost.parentNode) return;
      // Idempotent: if the widget is already lifted into the modal, do not
      // re-run the placeholder dance. Re-clicking a [data-rr-open-reviews]
      // trigger that lives inside the moved gallery (e.g. "See all
      // photos") used to insert a second placeholder INSIDE the modal
      // slot, then on close the widget would be restored to the modal
      // slot instead of the original inline location — leaving the inline
      // reviews area blank until reload.
      var alreadyOpen = inlinePlaceholder.parentNode || slot.contains(inlineHost);
      if (!alreadyOpen) {
        inlineHost.parentNode.insertBefore(inlinePlaceholder, inlineHost);
        inlineHost.classList.remove('reviews-redesign__yotpo--limited');
        inlineHost.classList.add('reviews-redesign__yotpo--in-modal');
        clearReviewLimit(inlineHost);
        // Gallery first so the photo carousel renders ABOVE the Yotpo
        // review list inside the modal body.
        moveGalleryIntoSlot();
        slot.appendChild(inlineHost);
      }

      if (typeof modal.showModal === 'function') {
        if (!modal.open) modal.showModal();
      } else {
        modal.setAttribute('open', '');
        modal.classList.add('rr-modal--fallback-open');
      }
      document.documentElement.classList.add('rr-modal-open');
    }

    function closeModal() {
      if (typeof modal.close === 'function' && modal.open) {
        modal.close();
      } else {
        modal.removeAttribute('open');
        modal.classList.remove('rr-modal--fallback-open');
      }
      document.documentElement.classList.remove('rr-modal-open');

      // Move the widget back to its original location.
      if (inlinePlaceholder.parentNode) {
        inlinePlaceholder.parentNode.insertBefore(inlineHost, inlinePlaceholder);
        inlinePlaceholder.parentNode.removeChild(inlinePlaceholder);
      }
      inlineHost.classList.remove('reviews-redesign__yotpo--in-modal');
      inlineHost.classList.add('reviews-redesign__yotpo--limited');
      applyReviewLimit(inlineHost, REVIEW_LIMIT);
      // Put the photo gallery back in its inline slot.
      restoreGalleryFromSlot();
    }

    openers.forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        openModal();
      });
    });
    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    if (backdrop) backdrop.addEventListener('click', closeModal);
    modal.addEventListener('cancel', function (e) { e.preventDefault(); closeModal(); });
    modal.addEventListener('close', function () {
      // Native close (ESC) — make sure we restore widget DOM AND the gallery.
      restoreGalleryFromSlot();
      if (slot.contains(inlineHost) && inlinePlaceholder.parentNode) {
        inlinePlaceholder.parentNode.insertBefore(inlineHost, inlinePlaceholder);
        inlinePlaceholder.parentNode.removeChild(inlinePlaceholder);
        inlineHost.classList.remove('reviews-redesign__yotpo--in-modal');
        inlineHost.classList.add('reviews-redesign__yotpo--limited');
        applyReviewLimit(inlineHost, REVIEW_LIMIT);
      }
      document.documentElement.classList.remove('rr-modal-open');
    });
  }

  // ============================================================
  // Yotpo data wiring (no-op until APP_KEY is set)
  // ============================================================
  function getProductId() {
    var widgetEl = document.querySelector('.reviews-redesign__yotpo .yotpo-widget-instance');
    return widgetEl && widgetEl.getAttribute('data-yotpo-product-id');
  }

  function wireYotpoData() {
    if (!YOTPO_APP_KEY) return;
    var productId = getProductId();
    if (!productId) return;

    fetchBottomlineAndReviews(productId);
    fetchSmartTopics(productId);
  }

  var PHOTO_CAP = 36;          // visual cap in carousel
  var MAX_REVIEW_PAGES = 8;    // hard ceiling: 8 × 150 = 1200 reviews
  var REVIEWS_PER_PAGE = 150;  // Yotpo widget API max

  // First page gives us bottomline + a chunk of reviews for verified % + photos.
  // Then we progressively page through more reviews to collect more photos.
  function fetchBottomlineAndReviews(productId) {
    fetchReviewsPage(productId, 1).then(function (firstPage) {
      var resp = firstPage && firstPage.response;
      var bl = resp && resp.bottomline;
      var reviews = (resp && resp.reviews) || [];

      if (bl) {
        renderBottomline(bl);
        renderSubRatings(bl.custom_fields_bottomline || {}, bl);
        renderAiSummaryFromStats(bl, reviews);
      }

      // Aggregate photos across pages; render incrementally as new ones land.
      var collected = collectMedia(reviews);
      renderPhotoCarousel(collected, /*incremental*/ false);

      var total = (bl && bl.total_review) || (resp && resp.pagination && resp.pagination.total) || 0;
      var totalPages = Math.min(Math.ceil(total / REVIEWS_PER_PAGE), MAX_REVIEW_PAGES);

      // Verified-buyer % is an OVERALL metric — it must reflect every
      // review, not just page 1. Accumulate verified counts as we page,
      // then render once at the end with the totals. If we can't fetch
      // every page (listing has more than MAX_REVIEW_PAGES × per_page
      // reviews), keep the stat hidden rather than show a sample-biased
      // number.
      var verifiedCount = reviews.filter(function (r) { return r.verified_buyer === true; }).length;
      var fetchedCount = reviews.length;
      function maybeRenderVerified() {
        if (total > 0 && fetchedCount >= total) {
          renderVerified(verifiedCount, fetchedCount);
        } else {
          // Couldn't fetch everything — don't ship a misleading number.
          hideVerified();
        }
      }
      if (totalPages <= 1 || collected.length >= PHOTO_CAP) {
        // Either we're done already (single-page listing) or we'll only
        // fetch enough pages to fill the carousel. Render verified % now
        // with whatever we have IF that's the full set.
        maybeRenderVerified();
        return;
      }

      // Page through 2..N SEQUENTIALLY and stop the moment PHOTO_CAP is
      // satisfied. The previous parallel-firing approach queued up to 7
      // extra cross-origin Yotpo requests (and 1000+ reviews of parsing)
      // for high-review products just to fill a 36-tile media strip,
      // even when page 2 already had enough photos. Sequential lets us
      // short-circuit on the first page that pushes us past the cap.
      function fetchNextPage(page) {
        if (page > totalPages || collected.length >= PHOTO_CAP) {
          maybeRenderVerified();
          return;
        }
        return fetchReviewsPage(productId, page)
          .then(function (data) {
            if (collected.length >= PHOTO_CAP) {
              maybeRenderVerified();
              return;
            }
            var more = (data && data.response && data.response.reviews) || [];
            // Accumulate verified counts across pages so the final % is
            // correct, not just the sample from page 1.
            verifiedCount += more.filter(function (r) { return r.verified_buyer === true; }).length;
            fetchedCount += more.length;
            var newMedia = collectMedia(more);
            if (newMedia.length) {
              collected = collected.concat(newMedia);
              renderPhotoCarousel(collected, /*incremental*/ true);
            }
            return fetchNextPage(page + 1);
          })
          .catch(function () { return fetchNextPage(page + 1); });
      }
      fetchNextPage(2);
    }).catch(function () { /* keep mocks on failure */ });
  }

  function fetchReviewsPage(productId, page) {
    var url = 'https://api-cdn.yotpo.com/v1/widget/' + YOTPO_APP_KEY +
              '/products/' + productId + '/reviews.json?per_page=' + REVIEWS_PER_PAGE + '&page=' + page;
    return fetch(url).then(function (r) { return r.json(); });
  }

  function collectMedia(reviews) {
    var media = [];
    reviews.forEach(function (rv) {
      var imgs = rv.images_data || rv.images || [];
      if (!Array.isArray(imgs)) return;
      // Pull review caption fields once per review so every image from
      // the same review carries the same title/body/author/score.
      var rvUser = rv.user || {};
      var caption = {
        title: rv.title || '',
        content: rv.content || rv.review_content || '',
        author: rvUser.display_name || rv.display_name || rv.user_name || '',
        score: typeof rv.score === 'number' ? rv.score : (typeof rv.review_score === 'number' ? rv.review_score : null),
        date: rv.created_at || rv.date_created || rv.published_at || ''
      };
      imgs.forEach(function (img) {
        // thumb for the tile bg (cheap), full for the lightbox (zoom-quality).
        var thumb = img.thumb_url || img.image_url || img.original_url;
        var full = img.original_url || img.image_url || img.thumb_url;
        if (thumb) media.push({
          url: thumb,
          fullUrl: full,
          type: (img.media_type || 'image'),
          reviewTitle: caption.title,
          reviewContent: caption.content,
          reviewAuthor: caption.author,
          reviewScore: caption.score,
          reviewDate: caption.date
        });
      });
    });
    return media;
  }

  function renderBottomline(bl) {
    setText('[data-yotpo-avg]', (bl.average_score || 0).toFixed(1));
    var avgPct = Math.min(100, Math.round(((bl.average_score || 0) / 5) * 100));
    document.querySelectorAll('[data-yotpo-avg-fill]').forEach(function (el) { el.style.width = avgPct + '%'; });
    setText('[data-yotpo-count]', bl.total_review || 0);

    var dist = bl.star_distribution || {};
    var total = bl.total_review || 1;
    [5, 4, 3, 2, 1].forEach(function (stars) {
      var count = dist[stars] || 0;
      var pct = Math.round((count / total) * 100);
      var row = document.querySelector('[data-bar-row="' + stars + '"]');
      if (!row) return;
      var fill = row.querySelector('.bar__fill');
      var c = row.querySelector('.bar-row__count');
      if (fill) fill.style.width = pct + '%';
      if (c) c.textContent = count;
    });
  }

  // Render N sub-rating circles from Yotpo Custom Questions, then append a
  // derived "Buyers recommend" circle computed from the star distribution
  // (matches Etsy's 4th badge — % of reviews ≥ 4 stars).
  function renderSubRatings(custom, bl) {
    var container = document.querySelector('[data-yotpo-subratings-container]');
    if (!container) return;
    var keys = Object.keys(custom);

    var html = keys.map(function (fieldId, i) {
      var f = custom[fieldId] || {};
      var score = (typeof f.average_score === 'number') ? f.average_score.toFixed(1) : '—';
      var title = escapeHtml(f.title || 'Rating ' + (i + 1));
      return '' +
        '<div class="sub-rating" data-sub-rating-index="' + i + '">' +
          '<div class="sub-rating__circle"><span>' + score + '</span></div>' +
          '<div class="sub-rating__label">' + title + '</div>' +
        '</div>';
    }).join('');

    // Derived: Buyers recommend = (4★ + 5★) / total. Skipped when we don't
    // have a star distribution (e.g., bl missing fields entirely).
    if (bl) {
      var dist = bl.star_distribution || {};
      var total = bl.total_review || 0;
      var positive = (dist[4] || 0) + (dist[5] || 0);
      if (total > 0) {
        var pct = Math.round((positive / total) * 100);
        html += '' +
          '<div class="sub-rating sub-rating--recommend" data-sub-rating-index="' + keys.length + '">' +
            '<div class="sub-rating__circle"><span>' + pct + '%</span></div>' +
            '<div class="sub-rating__label">Buyers recommend</div>' +
          '</div>';
      }
    }

    if (!html) {
      // Leave the container hidden — server-rendered with the `hidden`
      // attribute and no children — so shoppers see no metrics rather
      // than mock metrics when the API yields no usable subrating data.
      container.style.display = 'none';
      container.setAttribute('hidden', '');
      return;
    }
    container.innerHTML = html;
    container.style.gridTemplateColumns = '';
    // Real data landed — reveal.
    container.removeAttribute('hidden');
    container.style.display = '';
  }

  // Stats-only AI summary: real counts, real %, real top Custom Question.
  // Replaces the fictional "Subscribers consistently call this box…" prose.
  function renderAiSummaryFromStats(bl, reviews) {
    var textEl = document.querySelector('[data-yotpo-topics-text]');
    if (!textEl) return;
    var total = bl.total_review || 0;
    var avg = (bl.average_score || 0).toFixed(1);
    var dist = bl.star_distribution || {};
    var fiveStarCount = dist[5] || 0;
    var fiveStarPct = total ? Math.round((fiveStarCount / total) * 100) : 0;

    // Find the top-scoring Custom Question (e.g. "Quality 5.0/5")
    var custom = bl.custom_fields_bottomline || {};
    var topField = null;
    Object.keys(custom).forEach(function (k) {
      var f = custom[k];
      if (!f || typeof f.average_score !== 'number') return;
      if (!topField || f.average_score > topField.average_score) topField = f;
    });

    var parts = [];
    parts.push('Based on <strong>' + total + ' verified reviews</strong>');
    parts.push('average <strong>' + avg + '/5</strong>');
    if (fiveStarPct) parts.push('<strong>' + fiveStarPct + '%</strong> gave 5 stars');
    if (topField) {
      parts.push('top-rated factor: <strong>' + escapeHtml(topField.title) + ' (' + topField.average_score.toFixed(1) + '/5)</strong>');
    }
    textEl.innerHTML = parts.join(' · ') + '.';
  }

  // Verified-buyer % UI was removed — the bare "3%" / "96%" stat had no
  // label and rendered as an orphan number in the buyer-highlights row.
  // Stubs kept so existing call sites don't throw.
  function hideVerified() {}
  function renderVerified() {}

  // Build/update photo carousel from a media[] array.
  //   incremental=false → first paint (clear tiles, hide section if empty).
  //   incremental=true  → append new tiles only, preserving scroll position.
  function renderPhotoCarousel(media, incremental) {
    var carousel = document.querySelector('[data-rr-photo-carousel-track]');
    var gallery = document.querySelector('[data-yotpo-media-container]');
    if (!carousel) return;

    if (!media.length) {
      if (!incremental && gallery) gallery.style.display = 'none';
      return;
    }
    if (gallery) gallery.style.display = '';

    var photoCount = media.filter(function (m) { return m.type !== 'video'; }).length;
    var videoCount = media.filter(function (m) { return m.type === 'video'; }).length;
    setText('[data-yotpo-media-photos]', photoCount);
    setText('[data-yotpo-media-videos]', videoCount);

    var moreEl = document.querySelector('[data-yotpo-media-more]');
    // Exclude the +N "more" tile from the count — it ALSO carries the
    // .rr-photo-tile class but isn't a real photo, so counting it would
    // make the i = existing loop start one index past the last real
    // photo and silently skip a media tile on every incremental page.
    var existing = carousel.querySelectorAll('.rr-photo-tile:not(.rr-photo-tile--more)').length;

    if (!incremental) {
      // First paint — wipe placeholders and re-render up to PHOTO_CAP.
      carousel.innerHTML = '';
      existing = 0;
    }

    var cap = Math.min(media.length, PHOTO_CAP);
    // Stash the full media list on the carousel so the photo lightbox can
    // navigate prev/next across all tiles even when re-rendered.
    carousel._rrMedia = media.slice(0, cap);
    for (var i = existing; i < cap; i++) {
      var m = media[i];
      var tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'rr-photo-tile';
      tile.setAttribute('data-rr-photo-tile', '');
      tile.setAttribute('data-rr-photo-index', String(i));
      tile.setAttribute('data-rr-photo-url', m.url);
      tile.setAttribute('data-rr-photo-type', m.type || 'image');
      tile.setAttribute('aria-label', 'Open photo from review');
      tile.style.backgroundImage = 'url(' + m.url + ')';
      if (m.type === 'video') {
        tile.classList.add('rr-photo-tile--video');
        tile.innerHTML = '<span class="rr-photo-tile__play" aria-hidden="true">▶</span>';
      }
      // Insert before the +N "more" tile if present, otherwise append.
      if (moreEl && moreEl.parentNode === carousel) carousel.insertBefore(tile, moreEl);
      else carousel.appendChild(tile);
    }

    if (moreEl) {
      var remaining = media.length - cap;
      if (remaining > 0) {
        moreEl.textContent = '+' + remaining;
        moreEl.style.display = '';
        // First-paint wipe (carousel.innerHTML = '') detaches the +N tile.
        // Re-attach as the last child so it actually appears in the strip.
        if (moreEl.parentNode !== carousel) carousel.appendChild(moreEl);
      } else {
        moreEl.style.display = 'none';
      }
    }
  }

  function fetchSmartTopics(productId) {
    var pillsEl = document.querySelector('[data-yotpo-topics-pills]');
    var topicsEndpoints = [
      'https://api-cdn.yotpo.com/v1/widget/' + YOTPO_APP_KEY + '/products/' + productId + '/topics.json',
      'https://api.yotpo.com/v1/topics/' + YOTPO_APP_KEY + '?domain_key=' + productId
    ];
    topicsEndpoints.reduce(function (chain, url) {
      return chain.then(function (filled) {
        if (filled) return true;
        return fetch(url).then(function (r) { return r.ok ? r.json() : null; }).then(function (data) {
          var topics = (data && data.response && (data.response.topics || data.response.themes)) || (data && data.topics) || null;
          if (!topics || !topics.length) return false;
          if (!pillsEl) return false;
          pillsEl.innerHTML = topics.slice(0, 8).map(function (t) {
            var label = escapeHtml(t.title || t.name || t.label || '');
            return '<span class="highlight">' + label + '</span>';
          }).join('');
          // Reveal the parent line now that we have topics to show.
          var container = document.querySelector('[data-yotpo-topics-container]');
          if (container) container.removeAttribute('hidden');
          return true;
        }).catch(function () { return false; });
      });
    }, Promise.resolve(false)).then(function (filled) {
      // No Smart Topics data → hide the pills row + the filter pills row
      // (they're both fictional without it).
      if (filled) return;
      if (pillsEl) pillsEl.style.display = 'none';
      var filterPills = document.querySelector('.reviews-redesign__filter-bar .filter-pills');
      if (filterPills) filterPills.style.display = 'none';
    });
  }

  // ============================================================
  // Carousel scroll buttons
  // ============================================================
  function setupCarouselScroll() {
    var track = document.querySelector('[data-rr-photo-carousel-track]');
    var prev = document.querySelector('[data-rr-carousel-prev]');
    var next = document.querySelector('[data-rr-carousel-next]');
    if (!track) return;

    function scrollByTiles(direction) {
      var firstTile = track.querySelector('.rr-photo-tile, .gallery__tile');
      var step = firstTile ? firstTile.offsetWidth + 12 : 200;
      track.scrollBy({ left: direction * step * 3, behavior: 'smooth' });
    }
    if (prev) prev.addEventListener('click', function () { scrollByTiles(-1); });
    if (next) next.addEventListener('click', function () { scrollByTiles(1); });
  }

  // ============================================================
  // Content tabs — convert anchor tabs into in-place content switchers.
  // On init we move each section (#past-boxes, #questions, #details,
  // #more-products) into a tab panel inside the reviews card. Click a
  // tab → show only that panel. The "Reviews" tab keeps the existing
  // reviews UI as its panel.
  // ============================================================
  function setupContentTabs() {
    var card = document.querySelector('.reviews-redesign');
    var container = card && card.querySelector('.reviews-redesign__container');
    var tabsNav = container && container.querySelector('.reviews-redesign__tabs');
    if (!card || !container || !tabsNav) return;

    var titleEl = container.querySelector('.reviews-redesign__title');
    if (!titleEl) return;

    // 1. Wrap existing reviews content (everything in container after the
    //    title) into a "reviews" panel. This keeps DOM nodes stable so
    //    setupReviewsModal's references to .reviews-redesign__yotpo still work.
    var reviewsPanel = document.createElement('div');
    reviewsPanel.className = 'rr-tab-panel rr-tab-panel--reviews is-active';
    reviewsPanel.setAttribute('data-tab-panel', 'reviews');
    var nextEl = titleEl.nextElementSibling;
    while (nextEl) {
      var toMove = nextEl;
      nextEl = nextEl.nextElementSibling;
      // Skip the dialog modal — it stays as a sibling of the panels
      if (toMove.id === 'rr-reviews-modal') continue;
      reviewsPanel.appendChild(toMove);
    }
    container.appendChild(reviewsPanel);

    // 2. For each non-reviews tab target, locate the section and move it
    //    into a corresponding panel. Sections that don't exist (e.g. no
    //    past boxes for this product) cause that tab to be hidden.
    var tabSections = {
      'past-boxes': document.getElementById('past-boxes'),
      'questions': document.getElementById('questions'),
      'details': document.getElementById('details'),
      'more-products': document.getElementById('more-products')
    };

    Object.keys(tabSections).forEach(function (key) {
      var section = tabSections[key];
      if (!section) return;
      var panel = document.createElement('div');
      panel.className = 'rr-tab-panel rr-tab-panel--' + key;
      panel.setAttribute('data-tab-panel', key);
      panel.appendChild(section);
      container.appendChild(panel);
    });

    // Combined height of whatever sticky bars are pinned at the very top right now
    // (recovery mini-cart bar + top-anchored Add-to-cart CTA). Tab clicks must land
    // BELOW these, or the in-card tab strip ends up hidden behind them. Measured live
    // (CSS vars proved unreliable on mobile); the bottom-anchored mobile CTA is ignored.
    function pinnedTopOffset() {
      var off = 0;
      var rb = document.querySelector('.rb-bar[data-rb-variant="sticky"]');
      if (rb && getComputedStyle(rb).display !== 'none') {
        var r = rb.getBoundingClientRect();
        if (r.height && r.top < 60 && r.bottom > off) off = r.bottom;
      }
      var cta = document.querySelector('.pdp-sticky-cta.is-visible');
      if (cta) {
        var c = cta.getBoundingClientRect();
        if (c.height && c.top < 120 && c.bottom > off) off = c.bottom;
      }
      return off;
    }

    // 3. Wire tabs as panel switchers. Hide tabs whose section is missing.
    var tabs = Array.prototype.slice.call(tabsNav.querySelectorAll('.rr-tab'));
    tabs.forEach(function (tab) {
      var href = tab.getAttribute('href') || '';
      var key = href === '#reviews' ? 'reviews' : href.replace(/^#/, '');

      // Scroll-only tabs (e.g. Product Details): jump the user to the
      // target section that lives OUTSIDE the reviews card. Don't hide
      // and don't try to wire panel-switching — the section is already
      // permanently visible below the reviews block.
      if (tab.getAttribute('data-scroll-only') === 'true') {
        tab.addEventListener('click', function (e) {
          e.preventDefault();
          var target = document.querySelector(href);
          if (target) {
            var ty = target.getBoundingClientRect().top + (window.pageYOffset || 0) - pinnedTopOffset() - 12;
            window.scrollTo({ top: Math.max(0, ty), behavior: 'smooth' });
          }
        });
        return;
      }

      if (key !== 'reviews' && !tabSections[key]) {
        tab.style.display = 'none';
        return;
      }
      tab.addEventListener('click', function (e) {
        e.preventDefault();
        tabs.forEach(function (t) { t.classList.remove('rr-tab--active'); });
        tab.classList.add('rr-tab--active');
        container.querySelectorAll('.rr-tab-panel').forEach(function (p) {
          p.classList.remove('is-active');
        });
        var target = container.querySelector('[data-tab-panel="' + key + '"]');
        if (target) target.classList.add('is-active');

        // Hide the entire header row (title + Write A Review pill) on
        // non-reviews tabs. The pill is only meaningful in the Reviews
        // panel; leaving it visible while viewing Past Boxes / Questions
        // / etc. is confusing.
        var headerRow = container.querySelector('.rr-header-row');
        if (titleEl) {
          if (key === 'reviews') {
            titleEl.textContent = 'Reviews for this box';
            titleEl.style.display = '';
          } else {
            titleEl.style.display = 'none';
          }
        }
        if (headerRow) {
          headerRow.style.display = (key === 'reviews') ? '' : 'none';
        }

        // Scroll the card into view if user is far above/below it.
        var rect = card.getBoundingClientRect();
        if (rect.top < -10 || rect.top > window.innerHeight - 100) {
          var top = rect.top + (window.pageYOffset || 0) - pinnedTopOffset() - 12;
          window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        }
      });
    });

    // Delegated handler: any anchor pointing to #reviews / #details /
    // #past-boxes / #questions OUTSIDE the tabs nav should activate the
    // corresponding tab (not just scroll to whatever panel is currently
    // open). Example: the rating-link-row below the product title and
    // the Reviews CTA at the bottom of the cart form.
    // Every hash that maps to a tab panel inside the reviews card. If you
    // add another section that gets lifted into the tab nav (see
    // tabSections below), add its hash here too so external links / deep
    // links / fallback hrefs (e.g. seller card with no vendorUrl falling
    // back to #more-products) activate the tab instead of scrolling to a
    // hidden panel.
    var tabHrefs = {
      '#reviews': true,
      '#details': true,
      '#past-boxes': true,
      '#questions': true,
      '#more-products': true
    };
    once('contentTabsDelegated', function () {
      document.addEventListener('click', function (e) {
        var link = e.target.closest && e.target.closest('a[href]');
        if (!link) return;
        var href = link.getAttribute('href') || '';
        if (!tabHrefs[href]) return;
        // Skip the tab nav itself — those tabs have their own handlers.
        if (link.classList.contains('rr-tab') || link.closest('[data-sticky-tabs-inner]')) return;
        // Re-resolve `tabs` from the live card on each click in case the
        // section has been re-rendered by the theme editor since first init.
        var liveCard = document.querySelector('.reviews-redesign');
        var liveTabs = liveCard ? Array.prototype.slice.call(liveCard.querySelectorAll('.rr-tab')) : [];
        var matching = liveTabs.filter(function (t) { return t.getAttribute('href') === href; })[0];
        if (!matching || matching.style.display === 'none') return;
        e.preventDefault();
        matching.click();
        var rect = liveCard.getBoundingClientRect();
        var top = rect.top + (window.pageYOffset || 0) - pinnedTopOffset() - 12;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      });
    });

    // Apply the initial URL hash so deep links like .../products/foo#questions
    // (and back/forward to such anchors) open the right panel instead of
    // leaving the requested section hidden. The Reviews tab is active by
    // default, so #reviews and an empty hash are both no-ops.
    function applyHashToTab() {
      var hash = (window.location.hash || '').toLowerCase();
      if (!hash || hash === '#reviews') return;
      if (!tabHrefs[hash]) return;
      var matching = tabs.filter(function (t) { return t.getAttribute('href') === hash; })[0];
      if (!matching || matching.style.display === 'none') return;
      matching.click();
    }
    // Defer one tick so the panels are fully wired before the click fires.
    setTimeout(applyHashToTab, 0);
    once('hashChangeTab', function () {
      window.addEventListener('hashchange', function () {
        // Re-resolve tabs from the live DOM in case the section has been
        // re-rendered since the listener was bound.
        var liveCard = document.querySelector('.reviews-redesign');
        if (!liveCard) return;
        var hash = (window.location.hash || '').toLowerCase();
        if (!hash || hash === '#reviews') return;
        if (!tabHrefs[hash]) return;
        var matching = liveCard.querySelector('.rr-tab[href="' + hash + '"]');
        if (!matching || matching.style.display === 'none') return;
        matching.click();
      });
    });
  }

  // ============================================================
  // Sticky tabs row — pinned under the green sticky CTA when the
  // in-card tabs nav scrolls out of view. Clicks are forwarded to
  // the corresponding in-card tab so panel switching + active state
  // stay in one place (setupContentTabs).
  // ============================================================
  function setupStickyTabs() {
    var stickyHost = document.getElementById('pdp-sticky-tabs');
    var stickyInner = stickyHost && stickyHost.querySelector('[data-sticky-tabs-inner]');
    var card = document.querySelector('.reviews-redesign');
    var tabsNav = card && card.querySelector('.reviews-redesign__tabs');
    if (!stickyHost || !stickyInner || !tabsNav) return;

    var sourceTabs = Array.prototype.slice.call(tabsNav.querySelectorAll('.rr-tab'));
    if (!sourceTabs.length) return;

    sourceTabs.forEach(function (src) {
      if (src.style.display === 'none') return;
      var clone = src.cloneNode(true);
      clone.removeAttribute('id');
      clone.setAttribute('data-sticky-tab', '');
      clone.addEventListener('click', function (e) {
        e.preventDefault();
        src.click();
      });
      stickyInner.appendChild(clone);
    });

    var stickyTabs = Array.prototype.slice.call(stickyInner.querySelectorAll('.rr-tab'));

    function syncActive() {
      sourceTabs.forEach(function (src, i) {
        if (!stickyTabs[i]) return;
        stickyTabs[i].classList.toggle('rr-tab--active', src.classList.contains('rr-tab--active'));
      });
    }
    sourceTabs.forEach(function (src) {
      src.addEventListener('click', function () {
        // setupContentTabs flips the active class on click — mirror it here.
        setTimeout(syncActive, 0);
      });
    });
    syncActive();

    // Show only when the in-card reviews tabs nav has scrolled above the
    // viewport. Keeps the sticky tabs strictly scoped to the reviews
    // section — they should not overlay non-review content (e.g. the
    // buybox or gallery) just because the user has scrolled past Add
    // to Cart. Uses scroll + getBoundingClientRect (PDP-A pattern) for
    // the same editor-iframe reliability reason as the sticky CTA.
    function updateStickyTabsVisibility() {
      var liveTabsNav = document.querySelector('.reviews-redesign__tabs');
      if (!liveTabsNav) return;
      var r = liveTabsNav.getBoundingClientRect();
      var visible = r.bottom < 0;
      stickyHost.classList.toggle('is-visible', visible);
      stickyHost.setAttribute('aria-hidden', visible ? 'false' : 'true');
      if (visible) {
        // Sit below whatever sticky bars are pinned at the very top — measured LIVE
        // (not via CSS vars, which were unreliable on mobile) so the tab strip never
        // hides behind the recovery mini-cart bar or the top-anchored Add-to-cart bar
        // on any viewport. The bottom-anchored mobile CTA (top far down) is ignored.
        var topPx = 0;
        var rb = document.querySelector('.rb-bar[data-rb-variant="sticky"]');
        if (rb && getComputedStyle(rb).display !== 'none') {
          var rbr = rb.getBoundingClientRect();
          if (rbr.height && rbr.top < 60 && rbr.bottom > topPx) topPx = rbr.bottom;
        }
        var topCta = document.querySelector('.pdp-sticky-cta.is-visible');
        if (topCta) {
          var cbr = topCta.getBoundingClientRect();
          if (cbr.height && cbr.top < 120 && cbr.bottom > topPx) topPx = cbr.bottom;
        }
        stickyHost.style.setProperty('transform', 'translateY(' + Math.max(0, Math.round(topPx)) + 'px)', 'important');
      } else {
        stickyHost.style.removeProperty('transform');
      }
    }
    var stickyTabsTicking = false;
    function onStickyTabsScroll() {
      if (!stickyTabsTicking) {
        window.requestAnimationFrame(function () {
          stickyTabsTicking = false;
          updateStickyTabsVisibility();
        });
        stickyTabsTicking = true;
      }
    }
    once('stickyTabsScroll', function () {
      window.addEventListener('scroll', onStickyTabsScroll, { passive: true });
      window.addEventListener('resize', onStickyTabsScroll);
    });
    updateStickyTabsVisibility();

    // Sync the sticky tabs offset with the sticky CTA visibility — when
    // the CTA bar slides down at the top, push the tabs row below it;
    // when the CTA is hidden, tabs sit flush at the viewport top.
    // Two different observers control the two bars, so we mirror the
    // CTA's .is-visible class onto a marker class we react to in CSS.
    var ctaBar = document.querySelector('.pdp-sticky-cta');
    if (ctaBar) {
      function syncCtaState() {
        stickyHost.classList.toggle('rr-cta-above', ctaBar.classList.contains('is-visible'));
      }
      if (window.MutationObserver) {
        new MutationObserver(syncCtaState).observe(ctaBar, {
          attributes: true, attributeFilter: ['class']
        });
      }
      once('stickyTabsCtaSync', function () {
        window.addEventListener('scroll', function () {
          var liveStickyHost = document.getElementById('pdp-sticky-tabs');
          var liveCtaBar = document.querySelector('.pdp-sticky-cta');
          if (!liveStickyHost || !liveCtaBar) return;
          liveStickyHost.classList.toggle('rr-cta-above', liveCtaBar.classList.contains('is-visible'));
        }, { passive: true });
      });
      syncCtaState();
    }
  }

  // ============================================================
  // Photo lightbox — opens when a community-photo tile is clicked.
  // Builds its own DOM (so the section template stays untouched) and
  // navigates the same media list that renderPhotoCarousel cached on
  // the carousel element.
  // ============================================================
  function setupPhotoLightbox() {
    var carousel = document.querySelector('[data-rr-photo-carousel-track]');
    if (!carousel) return;
    // Idempotent: if the lightbox DOM is already in place (e.g. after a
    // shopify:section:load that re-renders the section), don't append a
    // second one. Most click handling on the carousel itself is delegated
    // via the photo-tile dataset, so the existing wiring still finds the
    // new tiles.
    if (document.querySelector('.rr-photo-lightbox')) return;

    var lb = document.createElement('div');
    lb.className = 'rr-photo-lightbox';
    lb.setAttribute('role', 'dialog');
    lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-hidden', 'true');
    lb.innerHTML = ''
      + '<button type="button" class="rr-photo-lightbox__close" data-rr-pl-close aria-label="Close">&times;</button>'
      + '<span class="rr-photo-lightbox__counter" data-rr-pl-counter></span>'
      + '<div class="rr-photo-lightbox__stage">'
      +   '<div class="rr-photo-lightbox__frame">'
      +     '<img class="rr-photo-lightbox__img" data-rr-pl-img src="" alt="">'
      +     '<video class="rr-photo-lightbox__video" data-rr-pl-video controls playsinline preload="metadata" hidden></video>'
      +     '<button type="button" class="rr-photo-lightbox__nav rr-photo-lightbox__nav--prev" data-rr-pl-prev aria-label="Previous">&lsaquo;</button>'
      +     '<button type="button" class="rr-photo-lightbox__nav rr-photo-lightbox__nav--next" data-rr-pl-next aria-label="Next">&rsaquo;</button>'
      +   '</div>'
      +   '<aside class="rr-photo-lightbox__caption" data-rr-pl-caption hidden>'
      +     '<header class="rr-photo-lightbox__caption-head">'
      +       '<span class="rr-photo-lightbox__caption-stars" data-rr-pl-stars aria-hidden="true"></span>'
      +       '<span class="rr-photo-lightbox__caption-author" data-rr-pl-author></span>'
      +       '<span class="rr-photo-lightbox__caption-date" data-rr-pl-date></span>'
      +     '</header>'
      +     '<h3 class="rr-photo-lightbox__caption-title" data-rr-pl-title></h3>'
      +     '<p class="rr-photo-lightbox__caption-body" data-rr-pl-body></p>'
      +   '</aside>'
      + '</div>';
    document.body.appendChild(lb);

    var imgEl = lb.querySelector('[data-rr-pl-img]');
    var videoEl = lb.querySelector('[data-rr-pl-video]');
    var counter = lb.querySelector('[data-rr-pl-counter]');
    var prevBtn = lb.querySelector('[data-rr-pl-prev]');
    var nextBtn = lb.querySelector('[data-rr-pl-next]');
    var closeBtn = lb.querySelector('[data-rr-pl-close]');
    var captionEl = lb.querySelector('[data-rr-pl-caption]');
    var capStarsEl = lb.querySelector('[data-rr-pl-stars]');
    var capAuthorEl = lb.querySelector('[data-rr-pl-author]');
    var capDateEl = lb.querySelector('[data-rr-pl-date]');
    var capTitleEl = lb.querySelector('[data-rr-pl-title]');
    var capBodyEl = lb.querySelector('[data-rr-pl-body]');
    var currentIdx = 0;

    // Always read from the LIVE carousel so the lightbox keeps working
    // after a shopify:section:load swap. The old `carousel` reference
    // captured at setup time would be detached after a re-render, and
    // renderPhotoCarousel writes `_rrMedia` onto the new node.
    function media() {
      var liveCarousel = document.querySelector('[data-rr-photo-carousel-track]');
      return (liveCarousel && liveCarousel._rrMedia) || [];
    }
    function renderStars(score) {
      if (typeof score !== 'number' || score <= 0) return '';
      var full = Math.round(score);
      var stars = '';
      for (var i = 1; i <= 5; i++) stars += (i <= full ? '★' : '☆');
      return stars;
    }
    function formatDate(s) {
      if (!s) return '';
      var d = new Date(s);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    }
    function show(idx) {
      var list = media();
      if (!list.length) return;
      currentIdx = ((idx % list.length) + list.length) % list.length;
      var m = list[currentIdx];
      // Render the right element for the media type. Videos use a real
      // <video> tag with controls — collected media items have type==='video'
      // for video reviews. Pause/reset any previous video on swap so audio
      // from the previous tile doesn't keep playing under a new image.
      if (videoEl) { try { videoEl.pause(); } catch (e) {} videoEl.removeAttribute('src'); videoEl.load && videoEl.load(); }
      if (m.type === 'video') {
        if (videoEl) {
          videoEl.src = m.fullUrl || m.url;
          videoEl.hidden = false;
        }
        imgEl.removeAttribute('src');
        imgEl.hidden = true;
      } else {
        // Prefer full-resolution URL so users can actually zoom in. Fall
        // back to the thumb URL if the API didn't return a full one.
        imgEl.src = m.fullUrl || m.url;
        imgEl.alt = m.alt || '';
        imgEl.hidden = false;
        if (videoEl) videoEl.hidden = true;
      }
      counter.textContent = (currentIdx + 1) + ' / ' + list.length;
      prevBtn.style.display = list.length > 1 ? '' : 'none';
      nextBtn.style.display = list.length > 1 ? '' : 'none';

      // Populate caption (review title + body + author + score) if available.
      // Show the caption block only when there's at least a title or body —
      // bare images without review text shouldn't render an empty panel.
      var hasContent = (m.reviewTitle && m.reviewTitle.length) ||
                       (m.reviewContent && m.reviewContent.length);
      if (hasContent) {
        if (capStarsEl) capStarsEl.textContent = renderStars(m.reviewScore);
        if (capAuthorEl) capAuthorEl.textContent = m.reviewAuthor || '';
        if (capDateEl) capDateEl.textContent = formatDate(m.reviewDate);
        if (capTitleEl) capTitleEl.textContent = m.reviewTitle || '';
        if (capBodyEl) capBodyEl.textContent = m.reviewContent || '';
        if (captionEl) captionEl.hidden = false;
      } else {
        if (captionEl) captionEl.hidden = true;
      }
    }
    function setZoomed(on) {
      lb.classList.toggle('is-zoomed', !!on);
      if (!on) imgEl.style.transformOrigin = '50% 50%';
    }
    // When the all-reviews <dialog> is open as a modal, it lives in the
    // browser top-layer and paints over everything else regardless of
    // z-index. A body-level lightbox would be hidden behind it — so we
    // reparent the lightbox INTO the open dialog for the lifetime of the
    // open call, then put it back on close.
    function getOpenDialogHost() {
      var dlg = document.getElementById('rr-reviews-modal');
      return (dlg && dlg.open) ? dlg : null;
    }
    function open(idx) {
      var host = getOpenDialogHost();
      if (host && lb.parentNode !== host) host.appendChild(lb);
      else if (!host && lb.parentNode !== document.body) document.body.appendChild(lb);
      show(idx);
      setZoomed(false);
      lb.classList.add('is-open');
      lb.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }
    function close() {
      lb.classList.remove('is-open');
      lb.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      setZoomed(false);
      // Stop any video playback so audio doesn't keep going after close.
      if (videoEl) { try { videoEl.pause(); } catch (e) {} videoEl.removeAttribute('src'); videoEl.load && videoEl.load(); }
      if (lb.parentNode !== document.body) document.body.appendChild(lb);
    }
    imgEl.addEventListener('click', function (e) {
      e.stopPropagation();
      var willZoom = !lb.classList.contains('is-zoomed');
      if (willZoom) {
        var r = imgEl.getBoundingClientRect();
        var ox = ((e.clientX - r.left) / r.width) * 100;
        var oy = ((e.clientY - r.top) / r.height) * 100;
        imgEl.style.transformOrigin = ox + '% ' + oy + '%';
      }
      setZoomed(willZoom);
    });
    imgEl.addEventListener('mousemove', function (e) {
      if (!lb.classList.contains('is-zoomed')) return;
      var r = imgEl.getBoundingClientRect();
      var ox = ((e.clientX - r.left) / r.width) * 100;
      var oy = ((e.clientY - r.top) / r.height) * 100;
      imgEl.style.transformOrigin = ox + '% ' + oy + '%';
    });

    // Delegated click on document — survives shopify:section:load that
    // replaces the carousel DOM, since we always re-resolve at fire time.
    document.addEventListener('click', function (e) {
      var tile = e.target.closest && e.target.closest('[data-rr-photo-tile]');
      if (!tile) return;
      // Only handle tiles inside the photo carousel track.
      if (!tile.closest('[data-rr-photo-carousel-track]')) return;
      e.preventDefault();
      e.stopPropagation();
      var idx = Number(tile.getAttribute('data-rr-photo-index'));
      if (Number.isNaN(idx)) idx = 0;
      open(idx);
    });

    lb.addEventListener('click', function (e) { if (e.target === lb) close(); });
    closeBtn.addEventListener('click', close);
    prevBtn.addEventListener('click', function (e) { e.stopPropagation(); setZoomed(false); show(currentIdx - 1); });
    nextBtn.addEventListener('click', function (e) { e.stopPropagation(); setZoomed(false); show(currentIdx + 1); });
    document.addEventListener('keydown', function (e) {
      if (!lb.classList.contains('is-open')) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') { setZoomed(false); show(currentIdx - 1); }
      else if (e.key === 'ArrowRight') { setZoomed(false); show(currentIdx + 1); }
    });
  }

  // ============================================================
  // Init
  // ============================================================
  // Mobile gallery — replaces the C-section's hero+thumbs strip with an
  // edge-to-edge swipeable carousel with dot pagination, Etsy-style.
  // Only active on D-template viewports ≤767px so the C-section's desktop
  // gallery (left filmstrip + main hero) is untouched.
  // ============================================================
  function setupMobileGallery() {
    if (window.matchMedia('(min-width: 768px)').matches) return;
    var pdpGallery = document.querySelector('.pdp-gallery');
    if (!pdpGallery) return;
    var thumbs = Array.prototype.slice.call(pdpGallery.querySelectorAll('.gallery-thumb'));
    if (!thumbs.length) {
      // Single image — fallback to making the existing hero full-bleed.
      pdpGallery.classList.add('rr-mobile-gallery-single');
      return;
    }

    // Build the strip from thumb data (each thumb has data-img-url for the
    // full-res image).
    var strip = document.createElement('div');
    strip.className = 'rr-mobile-gallery';
    var track = document.createElement('div');
    track.className = 'rr-mobile-gallery__track';
    var dotsHost = document.createElement('div');
    dotsHost.className = 'rr-mobile-gallery__dots';

    thumbs.forEach(function (thumb, i) {
      var slide = document.createElement('div');
      slide.className = 'rr-mobile-gallery__slide';
      slide.setAttribute('data-rr-mg-index', String(i));
      var pilotVideoEl = thumb.hasAttribute('data-video-thumb') && document.getElementById('pdp-pilot-video');
      if (pilotVideoEl) {
        // Video-pilot variant only (#pdp-pilot-video exists solely on the
        // pdp-video-pilot-video template; inert everywhere else): move the
        // real <video> into this slide so it is playable on phones — the
        // desktop hero that hosts it is display:none at this breakpoint.
        // The play/watch-75 beacon listeners travel with the element.
        slide.setAttribute('data-rr-mg-video', '1');
        pilotVideoEl.style.display = '';
        slide.appendChild(pilotVideoEl);
      } else {
        var img = document.createElement('img');
        img.src = thumb.dataset.imgUrl || (thumb.querySelector('img') && thumb.querySelector('img').src) || '';
        img.alt = (thumb.querySelector('img') && thumb.querySelector('img').alt) || '';
        img.loading = i === 0 ? 'eager' : 'lazy';
        slide.appendChild(img);
      }
      track.appendChild(slide);

      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'rr-mobile-gallery__dot' + (i === 0 ? ' is-active' : '');
      dot.setAttribute('aria-label', 'Go to image ' + (i + 1));
      dot.addEventListener('click', function () {
        var slideEl = track.querySelector('[data-rr-mg-index="' + i + '"]');
        if (slideEl) slideEl.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      });
      dotsHost.appendChild(dot);
    });

    strip.appendChild(track);
    strip.appendChild(dotsHost);

    // Open the photo lightbox on tap.
    track.addEventListener('click', function (e) {
      var slide = e.target.closest('[data-rr-mg-index]');
      if (!slide) return;
      // Taps on the video slide belong to the video's native controls —
      // forwarding them would play the video while opening the image lightbox.
      if (slide.hasAttribute('data-rr-mg-video')) return;
      var sourceThumb = thumbs[Number(slide.getAttribute('data-rr-mg-index'))];
      if (sourceThumb) sourceThumb.click();
      var zoom = pdpGallery.querySelector('.zoom-button, [data-gallery-zoom]');
      if (zoom) zoom.click();
    });

    // Insert before the original gallery-main; CSS hides .gallery-main on mobile.
    pdpGallery.insertBefore(strip, pdpGallery.firstChild);
    pdpGallery.classList.add('rr-mobile-gallery-active');

    // Update active dot on horizontal scroll.
    var dots = Array.prototype.slice.call(dotsHost.querySelectorAll('.rr-mobile-gallery__dot'));
    var raf = null;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        var idx = Math.round(track.scrollLeft / track.clientWidth);
        dots.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
      });
    }, { passive: true });
  }

  // Disabled — we now render Yotpo's native review card layout, so
  // there are no .rr-card-top slots to arrange. Kept as no-ops in case
  // anything else still calls them.
  function arrangeReviewerForViewport() {}
  function setupReviewerViewportArrange() {}

  // Inject Yotpo's character icon to the LEFT of each reviewer's name
  // in the native review card. We render the exact <div class="yotpo-
  // custom-icon" style="background-image: url(...)"> markup that prod
  // ships, since Yotpo's stock V3 widget doesn't ship this avatar.
  function injectReviewerAvatars() {
    var ICON_URL = 'https://cdn-widget-assets.yotpo.com/static_assets/ouJYuhvJ1tJMjMmMOd2zpKisG0fmlzUxFM05tDko/images/image_2023_08_10_21_42_29_176';

    function makeAvatar() {
      var a = document.createElement('div');
      a.className = 'yotpo-custom-icon';
      a.style.backgroundImage = "url('" + ICON_URL + "')";
      a.setAttribute('aria-hidden', 'true');
      a.setAttribute('data-rr-avatar', '1');
      return a;
    }

    function process() {
      try {
        var scope = document.querySelector('.reviews-redesign__yotpo')
                 || document.querySelector('.yotpo-widget-instance')
                 || document.body;

        // NUKE: wipe every existing avatar (mine + Yotpo's leftovers)
        // and every wrapper Yotpo uses for its native avatar. Fresh
        // slate every pass — no "is it duplicated?" guessing.
        scope.querySelectorAll(
          '.yotpo-custom-icon, .yotpo-reviewer-image, .yotpo-verified-image-icon'
        ).forEach(function (el) {
          if (el.parentNode) el.parentNode.removeChild(el);
        });

        // Find every reviewer-name element, then collapse to ONE per
        // review card. Card identity = the smallest ancestor that
        // contains the review TITLE. Each card has exactly one title,
        // so two names in the same card always resolve to the same
        // root — even if Yotpo emits both `yotpo-reviewer-name` AND
        // `yotpo-display-name` (which is what causes the duplicates).
        var names = scope.querySelectorAll('[class*="reviewer-name"], [class*="display-name"], [class*="user-name"], [class*="reviewer-display-name"]');
        var seenCards = [];

        function findCardRoot(nameEl) {
          var el = nameEl.parentElement;
          while (el && el !== scope) {
            if (el.querySelector(
              '[class*="review-title"], [class*="content-title"], [class*="review-content-title"]'
            )) return el;
            el = el.parentElement;
          }
          return nameEl.parentElement;
        }

        names.forEach(function (nameEl) {
          var card = findCardRoot(nameEl);
          if (!card) return;
          if (seenCards.indexOf(card) !== -1) return; // one avatar per card
          seenCards.push(card);

          var wrapper = nameEl.closest('[class*="reviewer-info"], [class*="user-info"], [class*="yotpo-header"], [data-testid*="reviewer"], [data-testid*="user"]')
                     || nameEl.parentElement;
          if (!wrapper) return;

          var avatar = makeAvatar();
          avatar.style.setProperty('display', 'block', 'important');
          avatar.style.setProperty('visibility', 'visible', 'important');
          avatar.style.setProperty('opacity', '1', 'important');
          // Force the wrapper into a 2-column grid so the avatar sits
          // to the LEFT of the name/verified-buyer stack, regardless
          // of whatever flex-column rules Yotpo or other CSS apply.
          avatar.style.setProperty('grid-column', '1', 'important');
          avatar.style.setProperty('grid-row', '1 / span 2', 'important');
          avatar.style.setProperty('align-self', 'center', 'important');
          wrapper.style.setProperty('display', 'grid', 'important');
          wrapper.style.setProperty('grid-template-columns', 'auto 1fr', 'important');
          wrapper.style.setProperty('column-gap', '10px', 'important');
          wrapper.style.setProperty('row-gap', '2px', 'important');
          wrapper.style.setProperty('align-items', 'start', 'important');
          wrapper.style.setProperty('justify-items', 'start', 'important');
          wrapper.insertBefore(avatar, wrapper.firstChild);
        });
      } catch (err) {
        // Silently swallow — never break the page just because the
        // avatar inject hit an unexpected DOM shape.
      }
    }

    // Multiple passes to catch Yotpo's initial render + late re-renders
    // across both desktop and mobile templates.
    process();
    [100, 300, 800, 1500, 2500, 5000, 10000].forEach(function (delay) {
      setTimeout(process, delay);
    });
  }

  // Force every "Verified Reviewer" label to read "Verified Buyer"
  // — Yotpo's mobile template renders the former, desktop the latter.
  // We want a single consistent label on every viewport.
  function normalizeReviewerText() {
    function fix() {
      var scope = document.querySelector('.reviews-redesign__yotpo');
      if (!scope) return;
      var nodes = scope.querySelectorAll('[class*="verified-reviewer"], [class*="verified-buyer"]');
      nodes.forEach(function (n) {
        // Skip if we already patched it.
        if (n.dataset && n.dataset.rrNormalized === '1') return;
        var inner = n.textContent;
        if (/verified\s*reviewer/i.test(inner)) {
          // Walk text nodes and replace just the text — don't blow away
          // child elements (icons, etc.) Yotpo might also put inside.
          var walker = document.createTreeWalker(n, NodeFilter.SHOW_TEXT, null);
          var t;
          while ((t = walker.nextNode())) {
            if (/verified\s*reviewer/i.test(t.nodeValue)) {
              t.nodeValue = t.nodeValue.replace(/verified\s*reviewer/i, 'Verified Buyer');
            }
          }
          n.dataset.rrNormalized = '1';
        }
      });
    }
    fix();
    [200, 600, 1500, 3500, 7000].forEach(function (d) { setTimeout(fix, d); });
  }

  // When Yotpo's Filters / Sort modal opens it renders BELOW our
  // sticky CTA + sticky tabs in z-index, covering the close (X) and
  // save controls. We hide both sticky bars while any overlay is up.
  //
  // Open detection: delegated click on the Filters / Sort trigger.
  // Close detection: POLL for modal disappearance from the DOM. We
  // tried watching close-button clicks but Yotpo's variants (X icon,
  // outside-tap, "Show N reviews" submit, swipe-down on mobile) don't
  // share one signal. Polling for the modal element is reliable.
  // Hide Yotpo filter controls — search reviews input, "Rating"
  // dropdown, "With media" toggle, and the mobile "Filters" button.
  // We search DOCUMENT-WIDE (not scoped to .reviews-redesign__yotpo)
  // because Yotpo can render these in portal-style detached nodes,
  // and observe document.body so we catch late additions.
  function setupFiltersHide() {
    // NOTE: do NOT include "verified buyers" here — that label exists
    // inside individual review cards (next to the customer name) and
    // matching it would walk up and hide the whole reviewer-info row,
    // taking the customer name with it.
    var FILTER_LABELS = /^(rating|with media|with photos?|with videos?|filters?|filter)$/;
    var isMobile = window.matchMedia('(max-width: 767px)').matches;

    function isInReviewsArea(el) {
      // Hard skip: anything inside the Questions widget (instance 462055)
      // is OUT of the reviews scope, even when the Questions panel has
      // been lifted into `.reviews-redesign` as a tab. Checking the
      // instance id FIRST is what makes this safe — a bare
      // `.reviews-redesign` early-return matched the Questions panel
      // after setupContentTabs moved it into the reviews card.
      var qWidget = el.closest('.yotpo-widget-instance[data-yotpo-instance-id="462055"]');
      if (qWidget) return false;
      // Reviews card chrome + all-reviews modal + the reviews Yotpo
      // widget itself (instance 345476).
      return !!el.closest('.reviews-redesign, .reviews-redesign__yotpo, .rr-modal, .yotpo-widget-instance[data-yotpo-instance-id="345476"]');
    }

    function hideAndCollapse(el) {
      el.style.setProperty('display', 'none', 'important');
      el.dataset.rrFilterHidden = '1';
      // Walk up — if parent is now empty (only display:none kids),
      // hide it too so we don't leave an empty toolbar row.
      var p = el.parentElement;
      while (p && p !== document.body) {
        var visible = false;
        for (var k = 0; k < p.children.length; k++) {
          if (p.children[k].style && p.children[k].style.display !== 'none') {
            visible = true;
            break;
          }
        }
        if (visible) break;
        p.style.setProperty('display', 'none', 'important');
        p = p.parentElement;
      }
    }

    function hide() {
      isMobile = window.matchMedia('(max-width: 767px)').matches;
      // Pass 1: search inputs (Search reviews placeholder).
      document.querySelectorAll('input').forEach(function (input) {
        if (input.dataset.rrFilterHidden === '1') return;
        if (!isInReviewsArea(input)) return;
        var ph = (input.getAttribute('placeholder') || '').toLowerCase();
        var type = (input.getAttribute('type') || '').toLowerCase();
        if (type === 'search' || /search/.test(ph)) {
          hideAndCollapse(input);
        }
      });
      // Pass 2: text-match Rating / With media / Filters labels.
      // Iterate leaf-ish nodes (≤3 children).
      // Same scoping rule as isInReviewsArea: drop anything that lives
      // inside the Questions widget (instance 462055), regardless of
      // whether it was moved under .reviews-redesign by the tab system.
      var roots = Array.prototype.slice.call(
        document.querySelectorAll('.reviews-redesign, .reviews-redesign__yotpo, .rr-modal, .yotpo-widget-instance[data-yotpo-instance-id="345476"]')
      ).filter(function (r) {
        return !r.closest('.yotpo-widget-instance[data-yotpo-instance-id="462055"]');
      });
      roots.forEach(function (root) {
        var all = root.querySelectorAll('*');
        for (var i = 0; i < all.length; i++) {
          var el = all[i];
          if (el.dataset && el.dataset.rrFilterHidden === '1') continue;
          if (el.children.length > 3) continue;
          var text = (el.textContent || '').trim().toLowerCase();
          if (text.length === 0 || text.length > 20) continue;
          if (!FILTER_LABELS.test(text)) continue;
          if (/^filters?$/.test(text) && !isMobile) continue;
          var target = el;
          while (target.parentElement && target.parentElement !== root) {
            if (target.parentElement.children.length >= 2) {
              target = target.parentElement;
              break;
            }
            target = target.parentElement;
          }
          hideAndCollapse(target);
        }
      });
      // Pass 3 (most reliable for the Rating + With media toolbar):
      // find a SINGLE wrapper that contains BOTH "rating" and "with
      // media" together and hide the whole thing. This works even
      // when Yotpo's actual control elements use class/text variants
      // our text-match passes don't catch.
      roots.forEach(function (root) {
        var all = root.querySelectorAll('div, section, form, ul');
        for (var j = 0; j < all.length; j++) {
          var w = all[j];
          if (w.dataset && w.dataset.rrFilterHidden === '1') continue;
          var t = (w.textContent || '').toLowerCase();
          // Wrapper must contain BOTH labels AND be short enough
          // (not a parent that wraps the entire reviews section).
          if (t.length > 200) continue;
          if (!/\brating\b/.test(t)) continue;
          if (!/with\s+media/.test(t)) continue;
          hideAndCollapse(w);
          break;
        }
      });
    }
    hide();
    // Observe entire body so portal-rendered Yotpo modals are caught,
    // but debounce: Yotpo hydration and filter-modal portal rendering
    // produce dozens of mutations per second, and an unthrottled
    // observer was firing the full hide() tree-walk on each one — visible
    // jank on slower mobile devices. Coalesce into a single rAF tick.
    var hideQueued = false;
    function queueHide() {
      if (hideQueued) return;
      hideQueued = true;
      var run = function () { hideQueued = false; hide(); };
      if (typeof window.requestAnimationFrame === 'function') {
        window.requestAnimationFrame(run);
      } else {
        setTimeout(run, 50);
      }
    }
    var obs = new MutationObserver(queueHide);
    obs.observe(document.body, { childList: true, subtree: true });
    once('filtersHideResize', function () {
      window.addEventListener('resize', queueHide);
    });
  }

  function setupYotpoModalStickyHide() {
    var pollTimer = null;
    var MODAL_SELECTORS = [
      '[class*="yotpo-filter-modal"]',
      '[class*="yotpo-modal"]',
      '[class*="filter-modal"]',
      '[class*="sort-modal"]',
      '[class*="bottom-sheet"]',
      '[class*="yotpo-drawer"]',
      '[data-testid*="filter-modal"]',
      '[data-testid*="sort-modal"]'
    ].join(',');

    function modalPresent() {
      var nodes = document.querySelectorAll(MODAL_SELECTORS);
      for (var i = 0; i < nodes.length; i++) {
        var n = nodes[i];
        var r = n.getBoundingClientRect();
        if (r.width > 50 && r.height > 50) return true;
      }
      // Fallback: body or html is locked by overflow hidden.
      if (document.body.style.overflow === 'hidden') return true;
      if (document.documentElement.style.overflow === 'hidden') return true;
      return false;
    }

    function setLocked(on) {
      document.documentElement.classList.toggle('rr-overlay-open', on);
      if (on) {
        if (pollTimer) clearInterval(pollTimer);
        // Poll for modal disappearance; release as soon as it's gone.
        pollTimer = setInterval(function () {
          if (!modalPresent()) {
            clearInterval(pollTimer);
            pollTimer = null;
            document.documentElement.classList.remove('rr-overlay-open');
          }
        }, 300);
      } else {
        if (pollTimer) {
          clearInterval(pollTimer);
          pollTimer = null;
        }
      }
    }

    // Delegated OPEN click: Filters / Sort trigger inside reviews.
    once('yotpoFilterClickGuard', function () {
      document.addEventListener('click', function (e) {
        var btn = e.target.closest('button, a, [role="button"]');
        if (!btn) return;
        var label = (btn.textContent || '').trim().toLowerCase();
        var aria = (btn.getAttribute('aria-label') || '').toLowerCase();
        var combined = label + ' ' + aria;
        var inReviews = !!btn.closest('.reviews-redesign__yotpo, .rr-modal');
        if (inReviews && /\b(filter|sort)/.test(combined)) {
          setLocked(true);
        }
      }, true);
    });

    // Escape closes most modal UIs — release immediately.
    once('yotpoFilterEscape', function () {
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && document.documentElement.classList.contains('rr-overlay-open')) {
          setLocked(false);
        }
      });
    });
  }

  // Move the "From the community" photo gallery to sit AFTER the
  // view-all button (matching Etsy's "Photos from reviews" placement
  // below the first batch of reviews). Pure DOM move — keeps event
  // wiring intact and avoids the cascade fights we hit with flex order.
  function moveGalleryBelowReviews() {
    var gallery = document.querySelector('.reviews-redesign__gallery');
    var viewAll = document.querySelector('.reviews-redesign__view-all');
    if (!gallery || !viewAll) return;
    if (gallery.dataset.rrMoved === '1') return;
    if (viewAll.nextSibling !== gallery) {
      viewAll.parentNode.insertBefore(gallery, viewAll.nextSibling);
    }
    gallery.dataset.rrMoved = '1';
  }

  // ============================================================
  // Message-seller CTA on the seller-trust card. The legacy
  // `dist.product.js` binds `[data-app-contact-seller-open]` via
  // `app.dom().on('click')`, which doesn't fire reliably on the
  // pdp-redesign-D template. Wire a delegated fallback ourselves:
  //   1) publish the `pdp-contact-seller-open` event if `window.app`
  //      exists (legacy ContactSeller subscribes to it and runs its own
  //      modal open + form reset);
  //   2) otherwise (and as a belt-and-braces fallback) just toggle
  //      `.inactive` on `#pdp-contact-seller` + `#modal`, matching what
  //      `app.dom(...).activate()` would do. Form submit still depends
  //      on ContactSeller having initialized, but at least the form is
  //      visible so the user can read/copy seller info.
  // ============================================================
  function setupMessageSellerCTA() {
    // The form/backdrop/close button are queried lazily inside openForm and
    // the close listeners so this function works after shopify:section:load
    // even though the original DOM references would be stale.
    once('messageSellerCTAClick', function () {
      function openForm() {
        // Always do the DOM toggle. Previously we early-returned after
        // publishing the legacy `pdp-contact-seller-open` event, but on
        // pdp-redesign-D the legacy ContactSeller subscriber doesn't
        // initialize reliably — so publish() succeeded silently and
        // nothing happened visually. Toggling .inactive on the form +
        // backdrop is idempotent (matches what app.dom().activate()
        // does internally) and guarantees the drawer opens regardless
        // of whether the legacy subscriber is bound.
        var app = window.app;
        if (app && app.event) {
          try { app.event('pdp-contact-seller-open').publish(); }
          catch (err) { /* ignore */ }
        }
        var form = document.getElementById('pdp-contact-seller');
        var backdrop = document.getElementById('modal');
        if (form) form.classList.remove('inactive');
        if (backdrop) backdrop.classList.remove('inactive');
      }
      function closeForm() {
        var form = document.getElementById('pdp-contact-seller');
        var backdrop = document.getElementById('modal');
        if (form) form.classList.add('inactive');
        if (backdrop) backdrop.classList.add('inactive');
      }
      document.addEventListener('click', function (e) {
        var trigger = e.target.closest && e.target.closest('[data-app-contact-seller-open]');
        if (!trigger) return;
        // Catch every contact-seller trigger on the page — new seller-trust
        // card, injected "Meet your seller" accordion, and the legacy bottom
        // "Contact seller" button — since the legacy delegated binder in
        // dist.product.js doesn't fire reliably on the D template. Falling
        // back here is idempotent: openForm() publishes via window.app first
        // (which is what the legacy subscriber does internally), and the
        // ContactSeller.activate path tolerates being called twice.
        e.preventDefault();
        openForm();
      });
      // Belt-and-braces close wiring: re-resolve close + backdrop at each
      // event so a section-load DOM swap doesn't leave us bound to stale
      // nodes. The close X is part of the form snippet, so it also moves
      // with section reloads.
      document.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('#pdp-contact-seller-form-close-btn')) {
          closeForm();
        }
        var backdrop = document.getElementById('modal');
        if (backdrop && e.target === backdrop) closeForm();
      });

      // Logged-out Message Seller flow: the seller-trust card and the
      // injected "Meet your seller" accordion render an <a> linking to
      // /account/login?return_url=/products/<handle>?action=pdp-contact-seller-open
      // The path+action is precomputed in Liquid and therefore drops any
      // ?variant= / ?selling_plan= / etc. URL state the shopper actually
      // had. Rebuild the return_url to use window.location so they land back
      // on the exact same configuration they were viewing.
      //
      // We rewrite in TWO places:
      //   1. On DOMContentLoaded (and after MutationObserver-detected
      //      injection — the accordion is built async by JS), so the
      //      rendered href reflects current URL state. This is what
      //      right-click → "open in new tab" / middle-click / browser
      //      context menus actually use, since those bypass the click
      //      event handler.
      //   2. On click as a defense-in-depth — covers the case where the
      //      URL state changed (variant swap via Liquid form) after the
      //      href was rewritten but before the user clicked.
      function rewriteOneHref(link) {
        try {
          var raw = link.getAttribute('href') || '';
          if (raw.indexOf('return_url=') === -1) return;
          var loginBase = raw.split('?')[0];
          var currentSearch = window.location.search || '';
          var joiner = currentSearch ? '&' : '?';
          var returnPath = window.location.pathname + currentSearch + joiner + 'action=pdp-contact-seller-open';
          link.setAttribute('href', loginBase + '?return_url=' + encodeURIComponent(returnPath));
        } catch (err) { /* leave the static href as-is */ }
      }
      function rewriteAllHrefs() {
        var links = document.querySelectorAll('a[href*="return_url="][href*="contact-seller"]');
        Array.prototype.forEach.call(links, rewriteOneHref);
      }
      // Initial pass.
      rewriteAllHrefs();
      // Re-rewrite when the buybox accordion gets injected, or whenever
      // the URL changes (e.g., variant swap updates ?variant=).
      var bbObserver = new MutationObserver(function () { rewriteAllHrefs(); });
      bbObserver.observe(document.body, { childList: true, subtree: true });
      window.addEventListener('popstate', rewriteAllHrefs);
      // Defense-in-depth click handler. Mostly redundant with the init
      // rewrite, but kicks in if the href hadn't been rewritten yet (e.g.
      // a link added between the last MutationObserver tick and the click).
      document.addEventListener('click', function (e) {
        var link = e.target.closest && e.target.closest('a[href*="return_url="][href*="contact-seller"]');
        if (!link) return;
        rewriteOneHref(link);
      });
    });
  }

  // ============================================================
  // Past/Current box image zoom — the legacy `dist.product.js` binds the
  // `.app--box-image-open` click handler via `app.dom(...).on('click')`
  // which doesn't fire reliably on the pdp-redesign-D template. Wire it
  // ourselves against the same DOM (`#pdp-box-image-modal` + the legacy
  // `.app--center-modal.inactive` toggle) so behavior matches prod.
  // ============================================================
  function setupBoxImageZoom() {
    var modal = document.getElementById('pdp-box-image-modal');
    var content = document.getElementById('pdp-box-image-modal--content');
    if (!modal || !content) return;
    // modal.addEventListener is per-DOM and runs every time setupBoxImageZoom
    // is called (e.g. after a section reload that re-renders the modal).
    // The document-level listeners must only attach once or they'll
    // double-fire after a theme-editor reload.
    modal.addEventListener('click', function (e) {
      if (e.target.closest('.app--box-image-close') || e.target === modal) {
        modal.classList.add('inactive');
        content.replaceChildren();
        document.documentElement.classList.remove('rr-modal-open');
      }
    });
    once('boxImageZoomClick', function () {
      document.addEventListener('click', function (e) {
        var img = e.target.closest && e.target.closest('.app--box-image-open');
        if (!img) return;
        // Only catch clicks inside the past/current box sections — don't
        // hijack `.app--box-image-open` elsewhere (e.g. legacy callers).
        var inBoxSection = !!img.closest('#past-boxes, #current-boxes');
        if (!inBoxSection) return;
        // Re-resolve modal at fire time so section-load swaps don't leave
        // us pointing at a detached node.
        var liveModal = document.getElementById('pdp-box-image-modal');
        var liveContent = document.getElementById('pdp-box-image-modal--content');
        if (!liveModal || !liveContent) return;
        e.preventDefault();
        var clone = img.cloneNode();
        clone.classList.remove('hover:scale-105');
        clone.classList.remove('cj-zoom-img');
        clone.removeAttribute('width');
        clone.removeAttribute('height');
        clone.style.maxWidth = '100%';
        clone.style.height = 'auto';
        clone.style.display = 'block';
        liveContent.replaceChildren(clone);
        liveModal.classList.remove('inactive');
        document.documentElement.classList.add('rr-modal-open');
      });
    });
    once('boxImageZoomEscape', function () {
      document.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        var liveModal = document.getElementById('pdp-box-image-modal');
        if (liveModal && !liveModal.classList.contains('inactive')) {
          liveModal.classList.add('inactive');
          var liveContent = document.getElementById('pdp-box-image-modal--content');
          if (liveContent) liveContent.replaceChildren();
          document.documentElement.classList.remove('rr-modal-open');
        }
      });
    });
  }

  // ============================================================
  function init() {
    injectBuyboxAccordions();
    var inline = setupInlineReviewLimit();
    setupReviewsModal(inline);
    setupCarouselScroll();
    setupContentTabs();
    setupStickyTabs();
    setupPhotoLightbox();
    setupMobileGallery();
    setupBoxImageZoom();
    setupMessageSellerCTA();
    moveGalleryBelowReviews();
    setupReviewerViewportArrange();
    setupYotpoModalStickyHide();
    setupFiltersHide();
    injectReviewerAvatars();
    normalizeReviewerText();
    wireYotpoData();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-run init when Shopify's theme editor re-renders the section. The
  // `once()` flags ensure document/window-level listeners don't double-bind,
  // while per-element setups (accordions, tabs, sticky tabs, gallery
  // placement, reviewer text normalization, etc.) attach fresh handlers to
  // the new DOM.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector &&
        (e.target.querySelector('.reviews-redesign') ||
         e.target.querySelector('.pdp-redesign'))) {
      init();
    }
  });

  function setText(selector, value) {
    document.querySelectorAll(selector).forEach(function (el) { el.textContent = value; });
  }
})();
